import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { defaultKeymap, history, historyKeymap, indentWithTab } from '@codemirror/commands';
import { html } from '@codemirror/lang-html';
import { javascript } from '@codemirror/lang-javascript';
import { json } from '@codemirror/lang-json';
import { bracketMatching, foldGutter, indentOnInput } from '@codemirror/language';
import { highlightSelectionMatches, searchKeymap } from '@codemirror/search';
import { Compartment, EditorState } from '@codemirror/state';
import { EditorView, drawSelection, keymap, lineNumbers } from '@codemirror/view';
import * as Dialog from '@radix-ui/react-dialog';
import { BookOpen, ChevronDown, Monitor, Pencil, Smartphone, Sparkles, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

import { AiAssistantPanel } from './AiAssistantPanel';
import { computeDiff, diffExtension, setDiffEffect } from './codemirror-diff';
import { templateLanguageExtension } from './codemirror-template-lang';
import { SyntaxGuidePanel } from './SyntaxGuidePanel';
import { buildVariableGroups, templateAutocomplete } from './template-autocomplete';

import type { DiffResult } from './codemirror-diff';
import type { VariableGroup } from './template-autocomplete';
import type {
  AnalyticsNodeMeta,
  AgentNodeMeta,
  ClassifierNodeMeta,
  QualityNodeMeta,
  ReviewNodeMeta,
} from './VariablePicker';

const SAMPLE_VALUES: Record<string, string> = {
  'call.id': 'call-abc-123',
  'call.direction': 'INBOUND',
  'call.channel': 'voice',
  'call.modality': 'VOICE',
  'call.outcome': 'LEAD_CAPTURED',
  'call.duration': '120',
  'call.phone_number': '+44 7700 900000',
  'contact.name': 'John Doe',
  'contact.phone': '+44 7700 900000',
  'campaign.name': 'Q1 Outreach',
  'campaign.id': 'camp-123',
  'config.notification_emails': 'admin@example.com',
  'review.reviewed_outcome': 'LEAD_CAPTURED',
  'review.ai_analysis': 'Customer showed strong buying intent.',
  'review.confidence_score': '0.92',
  'review.lead_data.customer_name': 'John Doe',
  'review.lead_data.vehicle_interest': '2024 BMW 3 Series',
  'review.lead_data.budget': '25,000',
  'review.lead_data.finance_preference': 'PCP Finance',
  'review.lead_data.additional_notes': 'Wants to visit this weekend.',
  'review.lead_data.customer_status': 'still_looking',
  'review.lead_data.selected_vehicle.vdp_url': 'https://example.com/car',
  'review.lead_data.selected_vehicle.name': '2024 BMW 3 Series',
  'review.lead_data.selected_vehicle.registration': 'AB12 CDE',
  'review.reviewed_lead_data.phone': '+44 7700 900000',
  'review.additional_review_data.should_send_callback_email': 'true',
  'review.additional_review_data.should_send_high_intent_email': 'true',
  'review.additional_review_data.callback_reason': 'Discuss trade-in options',
  'review.additional_review_data.buying_intent_level': '4',
  'review.callback_data.customer_question': 'What are the finance rates?',
  'review.callback_data.preferred_callback_time': 'Tomorrow afternoon',
  'review.enriched_vehicle.name': '2024 BMW 3 Series',
  'review.enriched_vehicle.checkout_url': 'https://example.com/reserve',
  'review.showroom_time.date': '25/04/2026 at 10:00',
  customer_name: 'John Doe',
  phone_number: '+44 7700 900000',
  vehicle_interest: '2024 BMW 3 Series',
  campaign_name: 'Q1 Outreach',
  company_name: 'Acme Corp',
  company_logo: '',
  dashboard_url: 'https://app.example.com/calls/call-abc-123',
  header_title: 'New Lead Alert',
  qualification_label: 'Fully Qualified',
  finance_preference: 'PCP Finance',
  additional_notes: 'Customer expressed strong interest.',
  agent_name: 'Alex (AI Agent)',
  should_send_callback_email: 'true',
  sms_sent: 'true',
  sms_not_sent: '',
};

function evaluatePreviewCondition(condition: string, vars: Record<string, string>): boolean {
  const eqMatch = condition.match(/^(.+?)\s*(==|!=)\s*(.+)$/);
  if (eqMatch) {
    const left = vars[eqMatch[1].trim()] ?? '';
    const rightRaw = eqMatch[3].trim();
    const right =
      (rightRaw.startsWith('"') && rightRaw.endsWith('"')) ||
      (rightRaw.startsWith("'") && rightRaw.endsWith("'"))
        ? rightRaw.slice(1, -1)
        : rightRaw;
    return eqMatch[2] === '==' ? left === right : left !== right;
  }
  const value = vars[condition];
  return !!value && value !== 'false' && value !== '';
}

function resolvePreviewExpression(expr: string, vars: Record<string, string>): string | null {
  const trimmed = expr.trim();
  if (
    (trimmed.startsWith('"') && trimmed.endsWith('"')) ||
    (trimmed.startsWith("'") && trimmed.endsWith("'"))
  ) {
    return trimmed.slice(1, -1);
  }
  const val = vars[trimmed];
  return val !== undefined && val !== '' ? val : null;
}

function resolvePreviewToken(expr: string, vars: Record<string, string>): string | null {
  const trimmed = expr.trim();

  // Ternary: var ? "a" : "b" or var == "x" ? "a" : "b"
  const qIdx = trimmed.indexOf('?');
  if (qIdx !== -1) {
    const condition = trimmed.slice(0, qIdx).trim();
    const rest = trimmed.slice(qIdx + 1);
    const cIdx = rest.indexOf(':');
    if (cIdx !== -1) {
      const trueBranch = rest.slice(0, cIdx).trim();
      const falseBranch = rest.slice(cIdx + 1).trim();
      const condResult = evaluatePreviewCondition(condition, vars);
      return resolvePreviewExpression(condResult ? trueBranch : falseBranch, vars) ?? '';
    }
  }

  // Fallback: var1 || var2 || "default"
  if (trimmed.includes('||')) {
    const parts = trimmed.split('||').map((p) => p.trim());
    for (const part of parts) {
      const val = resolvePreviewExpression(part, vars);
      if (val !== null) return val;
    }
    return null;
  }

  return resolvePreviewExpression(trimmed, vars);
}

function resolvePreview(template: string, vars: Record<string, string>): string {
  let result = template.replace(
    /\{\{#if\s+([^}]+)\}\}([\s\S]*?)(?:\{\{#else\}\}([\s\S]*?))?\{\{\/if\}\}/g,
    (_match, condition: string, ifContent: string, elseContent?: string) => {
      return evaluatePreviewCondition(condition.trim(), vars) ? ifContent : (elseContent ?? '');
    },
  );

  // Detect if a {{var}} is inside an HTML attribute to avoid injecting HTML badges
  result = result.replace(/\{\{([^}]+)\}\}/g, (_match, expr: string, offset: number) => {
    const resolved = resolvePreviewToken(expr, vars);
    if (resolved !== null) return resolved;

    const name = expr.trim();
    const before = result.slice(Math.max(0, offset - 200), offset);
    const inAttribute = /=\s*["'][^"']*$/.test(before);
    if (inAttribute) return `[${name}]`;
    return `<span style="background:#fef3c7;color:#92400e;padding:2px 6px;border-radius:3px;font-size:11px;font-family:ui-monospace,monospace;">${name}</span>`;
  });

  return result;
}

interface TemplateCodeEditorProps {
  value: string;
  onChange: (value: string) => void;
  type: 'email' | 'sms' | 'json' | 'javascript';
  subject?: string;
  onSubjectChange?: (value: string) => void;
  reviewSchema?: Record<string, unknown>;
  reviewNodeMetas?: ReviewNodeMeta[];
  classifierNodeMetas?: ClassifierNodeMeta[];
  qualityNodeMetas?: QualityNodeMeta[];
  agentNodeMetas?: AgentNodeMeta[];
  analyticsNodeMetas?: AnalyticsNodeMeta[];
  variableGroups?: VariableGroup[];
  aiContext?: 'prompt' | 'workflow_email' | 'workflow_sms' | 'workflow_json';
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const TemplateCodeEditor = ({
  value,
  onChange,
  type,
  subject,
  onSubjectChange,
  reviewSchema,
  reviewNodeMetas,
  classifierNodeMetas,
  qualityNodeMetas,
  agentNodeMetas,
  analyticsNodeMetas,
  variableGroups: variableGroupsProp,
  aiContext,
  open,
  onOpenChange,
}: TemplateCodeEditorProps) => {
  const viewRef = useRef<EditorView | null>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const valueRef = useRef(value);
  const onChangeRef = useRef(onChange);
  const autocompleteCompartment = useRef(new Compartment());
  const [width, setWidth] = useState<'desktop' | 'mobile'>('desktop');
  const [showGuide, setShowGuide] = useState(false);
  const [showVarEditor, setShowVarEditor] = useState(false);
  const [editableVars, setEditableVars] = useState<Record<string, string>>({});
  const [openChip, setOpenChip] = useState<string | null>(null);
  const [previewHtml, setPreviewHtml] = useState('');
  const [showAiPanel, setShowAiPanel] = useState(false);
  const [isReviewingDiff, setIsReviewingDiff] = useState(false);
  const [pendingDiff, setPendingDiff] = useState<DiffResult | null>(null);
  const originalContentRef = useRef<string>('');
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  valueRef.current = value;
  onChangeRef.current = onChange;

  const editorContext =
    aiContext ??
    (type === 'email'
      ? ('workflow_email' as const)
      : type === 'json'
        ? ('workflow_json' as const)
        : ('workflow_sms' as const));

  const effectiveSchema = useMemo(() => {
    if (reviewSchema) return reviewSchema;
    if (reviewNodeMetas && reviewNodeMetas.length > 0) {
      return reviewNodeMetas[0].schema;
    }
    return undefined;
  }, [reviewSchema, reviewNodeMetas]);

  const builtVariableGroups: VariableGroup[] = useMemo(
    () =>
      buildVariableGroups(
        effectiveSchema,
        undefined,
        classifierNodeMetas,
        qualityNodeMetas,
        agentNodeMetas,
        analyticsNodeMetas,
      ),
    [effectiveSchema, classifierNodeMetas, qualityNodeMetas, agentNodeMetas, analyticsNodeMetas],
  );

  const variableGroups = variableGroupsProp ?? builtVariableGroups;

  const mergedVars = useMemo(() => {
    const merged: Record<string, string> = { ...SAMPLE_VALUES };
    for (const [key, val] of Object.entries(editableVars)) {
      if (val !== undefined) merged[key] = val;
    }
    return merged;
  }, [editableVars]);

  const updatePreview = useCallback(
    (content: string) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        setPreviewHtml(resolvePreview(content, mergedVars));
      }, 300);
    },
    [mergedVars],
  );

  useEffect(() => {
    updatePreview(valueRef.current);
  }, [mergedVars, updatePreview]);

  const handleProposedEdit = useCallback((proposedContent: string) => {
    const current = valueRef.current;
    originalContentRef.current = current;
    const diff = computeDiff(current, proposedContent);

    if (!diff.hasChanges) return;

    setPendingDiff(diff);
    setIsReviewingDiff(true);

    if (viewRef.current) {
      viewRef.current.dispatch({
        effects: setDiffEffect.of(diff),
      });
    }
  }, []);

  const handleAcceptDiff = useCallback(() => {
    if (!pendingDiff || !viewRef.current) return;

    const view = viewRef.current;
    view.dispatch({
      changes: {
        from: 0,
        to: view.state.doc.length,
        insert: pendingDiff.proposedContent,
      },
      effects: setDiffEffect.of(null),
    });

    onChangeRef.current(pendingDiff.proposedContent);
    updatePreview(pendingDiff.proposedContent);

    setIsReviewingDiff(false);
    setPendingDiff(null);
  }, [pendingDiff, updatePreview]);

  const handleRejectDiff = useCallback(() => {
    if (viewRef.current) {
      viewRef.current.dispatch({
        effects: setDiffEffect.of(null),
      });
    }
    setIsReviewingDiff(false);
    setPendingDiff(null);
  }, []);

  const editorRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (viewRef.current) {
        viewRef.current.destroy();
        viewRef.current = null;
      }

      if (!node || !open) return;

      const state = EditorState.create({
        doc: valueRef.current,
        extensions: [
          lineNumbers(),
          history(),
          drawSelection(),
          bracketMatching(),
          foldGutter(),
          indentOnInput(),
          highlightSelectionMatches(),
          keymap.of([indentWithTab, ...defaultKeymap, ...historyKeymap, ...searchKeymap]),
          type === 'json' ? json() : type === 'javascript' ? javascript() : html(),
          templateLanguageExtension(),
          diffExtension(),
          autocompleteCompartment.current.of(templateAutocomplete(variableGroups)),
          EditorView.updateListener.of((update) => {
            if (update.docChanged) {
              const content = update.state.doc.toString();
              onChangeRef.current(content);
              updatePreview(content);
            }
          }),
          EditorView.lineWrapping,
          EditorView.theme({
            '&': { height: '100%', backgroundColor: '#1e1e2e' },
            '.cm-scroller': { overflow: 'auto' },
            '.cm-content': {
              fontFamily: 'ui-monospace, monospace',
              fontSize: '13px',
              color: '#cdd6f4',
              caretColor: '#f5e0dc',
            },
            '.cm-cursor, .cm-dropCursor': {
              borderLeftColor: '#f5e0dc',
              borderLeftWidth: '2px',
            },
            '.cm-gutters': {
              backgroundColor: '#181825',
              color: '#6c7086',
              borderRight: '1px solid #313244',
            },
            '.cm-activeLineGutter': { backgroundColor: '#313244' },
            '.cm-activeLine': { backgroundColor: '#313244' },
            '.cm-selectionBackground, ::selection': { backgroundColor: '#45475a !important' },
            '.cm-tooltip': {
              backgroundColor: '#1e1e2e',
              color: '#cdd6f4',
              border: '1px solid #45475a',
            },
            '.cm-tooltip-autocomplete': {
              backgroundColor: '#1e1e2e',
              border: '1px solid #45475a',
            },
            '.cm-tooltip-autocomplete ul li': { color: '#cdd6f4' },
            '.cm-tooltip-autocomplete ul li[aria-selected]': {
              backgroundColor: '#45475a',
              color: '#cdd6f4',
            },
            '.cm-completionLabel': { color: '#89b4fa' },
            '.cm-completionDetail': { color: '#6c7086', fontStyle: 'italic' },
            '.cm-completionMatchedText': { color: '#f9e2af', textDecoration: 'none' },
          }),
        ],
      });

      const view = new EditorView({ state, parent: node });
      viewRef.current = view;
      updatePreview(valueRef.current);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [open],
  );

  useEffect(() => {
    if (viewRef.current) {
      viewRef.current.dispatch({
        effects: autocompleteCompartment.current.reconfigure(templateAutocomplete(variableGroups)),
      });
    }
  }, [variableGroups]);

  useEffect(() => {
    if (!open && viewRef.current) {
      viewRef.current.destroy();
      viewRef.current = null;
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (iframeRef.current) {
      const doc = iframeRef.current.contentDocument;
      if (doc) {
        doc.open();
        if (type === 'email') {
          doc.write(previewHtml);
        } else if (type === 'json') {
          let formatted = previewHtml;
          try {
            formatted = JSON.stringify(JSON.parse(previewHtml), null, 2);
          } catch {
            // not valid JSON — show raw
          }
          doc.write(
            `<pre style="font-family:ui-monospace,monospace;white-space:pre-wrap;padding:16px;font-size:12px;">${formatted}</pre>`,
          );
        } else {
          doc.write(
            `<pre style="font-family:system-ui;white-space:pre-wrap;padding:16px;">${previewHtml}</pre>`,
          );
        }
        doc.close();
      }
    }
  }, [previewHtml, open, type]);

  const insertVariable = (path: string) => {
    const view = viewRef.current;
    if (!view) return;
    const insertion = `{{${path}}}`;
    const { from } = view.state.selection.main;
    view.dispatch({ changes: { from, insert: insertion } });
    view.focus();
    setOpenChip(null);
  };

  const templateVarNames = useMemo(() => {
    const matches = value.match(/\{\{([^}]+)\}\}/g) || [];
    const vars: string[] = [];

    for (const m of matches) {
      let inner = m.replace(/\{\{|\}\}/g, '').trim();
      if (inner === '/if' || inner === '#else') continue;

      // Extract variable from {{#if var}} or {{#if var == "val"}}
      if (inner.startsWith('#if ')) {
        inner = inner.slice(4).trim();
        const compIdx = inner.search(/\s*(==|!=)\s*/);
        if (compIdx !== -1) inner = inner.slice(0, compIdx).trim();
        if (inner) vars.push(inner);
        continue;
      }

      // Extract variable from ternary: var ? "a" : "b" or var == "x" ? "a" : "b"
      const qIdx = inner.indexOf('?');
      if (qIdx !== -1) {
        let cond = inner.slice(0, qIdx).trim();
        const compIdx = cond.search(/\s*(==|!=)\s*/);
        if (compIdx !== -1) cond = cond.slice(0, compIdx).trim();
        if (cond) vars.push(cond);
        continue;
      }

      // Extract variable from fallback: var || "default"
      if (inner.includes('||')) {
        const parts = inner.split('||').map((p) => p.trim());
        for (const p of parts) {
          if (!p.startsWith('"') && !p.startsWith("'") && p) vars.push(p);
        }
        continue;
      }

      vars.push(inner);
    }

    return [...new Set(vars)];
  }, [value]);

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[60] bg-black/60" />
        <Dialog.Content className="bg-background fixed inset-0 z-[61] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between border-b px-4 py-2">
            <Dialog.Title className="text-sm font-semibold">Template Editor</Dialog.Title>
            <div className="flex items-center gap-2">
              <Button
                variant={showVarEditor ? 'default' : 'outline'}
                size="sm"
                className="h-7 text-xs"
                onClick={() => setShowVarEditor(!showVarEditor)}
              >
                <Pencil className="mr-1 h-3 w-3" />
                Edit Values
              </Button>
              <Button
                variant={showGuide ? 'default' : 'outline'}
                size="sm"
                className="h-7 text-xs"
                onClick={() => setShowGuide(!showGuide)}
              >
                <BookOpen className="mr-1 h-3 w-3" />
                Syntax Guide
              </Button>
              <Button
                variant={showAiPanel ? 'default' : 'outline'}
                size="sm"
                className="h-7 text-xs"
                onClick={() => setShowAiPanel(!showAiPanel)}
                disabled={isReviewingDiff}
              >
                <Sparkles className="mr-1 h-3 w-3" />
                AI Edit
              </Button>
              {type === 'email' && (
                <div className="flex gap-1">
                  <Button
                    size="sm"
                    className="h-7"
                    variant={width === 'desktop' ? 'default' : 'outline'}
                    onClick={() => setWidth('desktop')}
                  >
                    <Monitor className="h-3 w-3" />
                  </Button>
                  <Button
                    size="sm"
                    className="h-7"
                    variant={width === 'mobile' ? 'default' : 'outline'}
                    onClick={() => setWidth('mobile')}
                  >
                    <Smartphone className="h-3 w-3" />
                  </Button>
                </div>
              )}
              {(type === 'json' || type === 'javascript') && (
                <span className="text-muted-foreground rounded border px-2 py-0.5 font-mono text-[10px]">
                  {type === 'javascript' ? 'JS' : 'JSON'}
                </span>
              )}
              <Dialog.Close asChild>
                <Button variant="ghost" size="icon" className="h-7 w-7">
                  <X className="h-4 w-4" />
                </Button>
              </Dialog.Close>
            </div>
          </div>

          {/* Subject bar (email only, not json) */}
          {type === 'email' && onSubjectChange && (
            <div className="flex items-center gap-2 border-b px-4 py-1.5">
              <span className="text-muted-foreground text-xs font-medium">Subject:</span>
              <input
                className="border-input bg-background flex-1 rounded border px-2 py-1 text-sm"
                value={subject ?? ''}
                onChange={(e) => onSubjectChange(e.target.value)}
                placeholder="Email subject..."
              />
            </div>
          )}

          {/* Diff review bar */}
          {isReviewingDiff && (
            <div className="flex items-center gap-2 border-b bg-amber-50 px-4 py-1.5 dark:bg-amber-950/30">
              <span className="text-xs font-medium text-amber-800 dark:text-amber-200">
                AI proposed changes — review the diff below
              </span>
              <div className="ml-auto flex gap-2">
                <Button size="sm" className="h-7 text-xs" onClick={handleAcceptDiff}>
                  Accept
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 text-xs"
                  onClick={handleRejectDiff}
                >
                  Reject
                </Button>
              </div>
            </div>
          )}

          {/* Main split view */}
          <div className="relative flex min-h-0 flex-1 overflow-hidden">
            {/* Editor pane */}
            <div className="min-w-0 flex-1 overflow-hidden" ref={editorRef} />

            {/* Preview pane */}
            <div className="flex min-w-0 flex-1 flex-col overflow-hidden border-l">
              <div className="bg-muted/50 border-b px-3 py-1.5">
                <span className="text-muted-foreground text-xs font-medium">Preview</span>
              </div>
              <div className="flex flex-1 justify-center overflow-auto bg-gray-100 p-4 dark:bg-gray-900">
                <iframe
                  ref={iframeRef}
                  title="Template Preview"
                  className="rounded border bg-white shadow-sm"
                  style={{
                    width: type === 'sms' ? 375 : width === 'desktop' ? '100%' : 375,
                    maxWidth: type === 'sms' ? 375 : width === 'desktop' ? 700 : 375,
                    height: '100%',
                    minHeight: 400,
                  }}
                  sandbox="allow-same-origin"
                />
              </div>
            </div>

            {/* Variable value editor panel */}
            {showVarEditor && (
              <div className="w-64 shrink-0 overflow-y-auto border-l p-3">
                <div className="mb-3 flex items-center justify-between">
                  <span className="text-xs font-semibold">Sample Values</span>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-5 w-5"
                    onClick={() => setShowVarEditor(false)}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
                <div className="space-y-2">
                  {templateVarNames.map((name) => (
                    <div key={name} className="space-y-0.5">
                      <label className="text-muted-foreground block truncate font-mono text-[10px]">
                        {name}
                      </label>
                      <Input
                        value={editableVars[name] ?? mergedVars[name] ?? ''}
                        onChange={(e) =>
                          setEditableVars((prev) => ({ ...prev, [name]: e.target.value }))
                        }
                        className="h-7 text-xs"
                      />
                    </div>
                  ))}
                  {templateVarNames.length === 0 && (
                    <p className="text-muted-foreground text-xs italic">
                      No variables found in template
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Syntax guide panel */}
            <SyntaxGuidePanel open={showGuide} onClose={() => setShowGuide(false)} />

            {/* AI Assistant panel - floating sidebar */}
            {showAiPanel && (
              <AiAssistantPanel
                currentContent={valueRef.current}
                availableVariables={variableGroups.flatMap((g) =>
                  g.variables.map((v) => ({ path: v.path, description: v.label })),
                )}
                editorContext={editorContext}
                onProposedEdit={handleProposedEdit}
                onClose={() => setShowAiPanel(false)}
              />
            )}
          </div>

          {/* Bottom bar — variable chips + save */}
          <div className="flex items-center justify-between border-t px-4 py-2">
            <div className="flex flex-wrap items-center gap-1">
              {variableGroups.map((g) => (
                <div key={g.namespace} className="relative">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 text-[11px]"
                    onClick={() => setOpenChip(openChip === g.namespace ? null : g.namespace)}
                  >
                    {g.namespace}
                    <ChevronDown className="ml-1 h-3 w-3" />
                  </Button>
                  {openChip === g.namespace && (
                    <>
                      <div className="fixed inset-0 z-[70]" onClick={() => setOpenChip(null)} />
                      <div className="border-border bg-popover text-popover-foreground absolute bottom-full left-0 z-[71] mb-1 max-h-56 w-60 overflow-y-auto rounded-md border shadow-lg">
                        {g.variables.map((v) => (
                          <button
                            key={v.path}
                            type="button"
                            className="hover:bg-accent hover:text-accent-foreground w-full px-3 py-1.5 text-left font-mono text-[11px]"
                            onClick={() => insertVariable(v.path)}
                          >
                            {v.path}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
            <Button size="sm" onClick={() => onOpenChange(false)}>
              Save & Close
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
